import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import "../../../styles/components/dialogs/accounts/manageAccountsDialogComponent.scss";
import AlertService from "../../../services/alerts/alert.service";
import agentService from "../../../services/agents/agent.service";
import validationHelper from "../../../helpers/validation.helper";
import TextInputElementComponent from "../../../components/general/elements/inputElements/TextInputElementComponent";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import useAgentListQuery from "../../../hooks/queries/useAgentListQuery";
import useDelayedFlag from "../../../hooks/useDelayedFlag";
import { SkeletonTable } from "../../../components/general/skeleton";

export const ManageAgentsDialogComponent = () => {
  const [, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");

  // 0016: the agent list now comes from the shared React Query hook (one deduped fetch, cache shared
  // with the edit dialog). After each mutation below, refetch() re-runs the query immediately — so
  // the table reflects the change at once even under the global 60s staleTime (refetch ignores it).
  const { data: agents = [], isLoading, isError, refetch: refetchAgents } = useAgentListQuery();

  // 0018: skeleton rows on the initial load only (isLoading is false once the list is cached),
  // gated by ~120ms so a cached re-open of the dialog shows the table instantly.
  const showSkeleton = useDelayedFlag(isLoading);

  useEffect(() => {
    if (isError) {
      AlertService.error("טעינת הסוכנים נכשלה", "אנא נסה שנית מאוחר יותר");
    }
  }, [isError]);

  const handleToggleActive = async (agent) => {
    try {
      const response = await agentService.setAgentActive(agent.id, !agent.isActive);
      if (!validationHelper.validateHttpResponse(response.data)) {
        AlertService.error("עדכון סטטוס הסוכן נכשל", "אנא נסה שנית מאוחר יותר");
        return;
      }
      refetchAgents();
    } catch (error) {
      AlertService.error("עדכון סטטוס הסוכן נכשל", "אנא נסה שנית מאוחר יותר");
      console.error("Error toggling agent active state:", error);
    }
  };

  const handleTogglePermissions = async (agent) => {
    try {
      const response = await agentService.setAgentPermissions(agent.id, !agent.canAddCustomers);
      if (!validationHelper.validateHttpResponse(response.data)) {
        AlertService.error("עדכון הרשאות הסוכן נכשל", "אנא נסה שנית מאוחר יותר");
        return;
      }
      refetchAgents();
    } catch (error) {
      AlertService.error("עדכון הרשאות הסוכן נכשל", "אנא נסה שנית מאוחר יותר");
      console.error("Error toggling agent permissions:", error);
    }
  };

  const handleAssign = (agentId) => {
    const queryParams = new URLSearchParams();
    queryParams.set("d", "agent");
    queryParams.set("action", "assign");
    queryParams.set("id", agentId);
    setSearchParams(queryParams);
  };

  // 0006: open the create/edit dialog in EDIT mode (prefilled from the agent list).
  const handleEdit = (agentId) => {
    const queryParams = new URLSearchParams();
    queryParams.set("d", "agent");
    queryParams.set("action", "add");
    queryParams.set("id", agentId);
    setSearchParams(queryParams);
  };

  // 0006: admin-initiated password reset (a temporary password is emailed to the agent).
  const handleResetPassword = async (agentId) => {
    const userChoice = await AlertService.confirm("לאפס את סיסמת הסוכן?", "סיסמה זמנית תישלח לכתובת הדואר האלקטרוני של הסוכן", "כן אפס", "בטל");
    if (!userChoice) return;

    try {
      const response = await agentService.resetAgentPassword(agentId);
      if (!validationHelper.validateHttpResponse(response.data)) {
        AlertService.error("איפוס הסיסמה נכשל", "אנא נסה שנית מאוחר יותר");
        return;
      }
      AlertService.success("סיסמה זמנית נשלחה לסוכן");
    } catch (error) {
      AlertService.error("איפוס הסיסמה נכשל", "אנא נסה שנית מאוחר יותר");
      console.error("Error resetting agent password:", error);
    }
  };

  const getAgentFullName = (agent) => {
    const fullName = `${agent.firstName || ""} ${agent.lastName || ""}`.trim();
    return fullName || "—";
  };

  const handleDelete = async (agentId) => {
    const userChoice = await AlertService.confirm("האם אתה בטוח שברצונך למחוק סוכן זה?", "שים לב, פעולה זו בלתי הפיכה", "כן מחק", "בטל");
    if (!userChoice) return;

    try {
      const response = await agentService.deleteAgent(agentId);
      if (!validationHelper.validateHttpResponse(response.data)) {
        AlertService.error("מחיקת הסוכן נכשלה", "אנא נסה שנית מאוחר יותר");
        return;
      }
      AlertService.success("הסוכן נמחק בהצלחה");
      refetchAgents();
    } catch (error) {
      AlertService.error("מחיקת הסוכן נכשלה", "אנא נסה שנית מאוחר יותר");
      console.error("Error deleting agent:", error);
    }
  };

  const getNewAgentContainerElement = () => {
    return (
      <div className="newAccountContainer">
        <Link to="?d=agent&action=add" className="addAccountButton primaryButton">
          הוספת סוכן
        </Link>
      </div>
    );
  };

  const getSearchInput = () => {
    const inputProps = {
      ...textInputModel,
      name: "searchInput",
      value: searchTerm,
      placeholder: "חפש סוכן...",
      onChange: (e) => setSearchTerm(e.target.value),
      icon: <i className="fa-solid fa-magnifying-glass"></i>,
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getExistingAgentsContainerElement = () => {
    const filteredAgents = agents?.filter((agent) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const email = agent.email?.toLowerCase() || "";
      const name = `${agent.firstName || ""} ${agent.lastName || ""}`.toLowerCase();
      return email.includes(term) || name.includes(term);
    });

    return (
      <div className="existingAccountTable">
        <div className="tableHeader">
          <div>שם</div>
          <div>דוא"ל</div>
          <div>פעולות</div>
        </div>
        <div className="tableBody">
          {isLoading && showSkeleton && <SkeletonTable rows={6} cols={3} showHeader={false} />}
          {!isLoading && filteredAgents?.map((agent) => (
            <div key={agent.id} className="tableRow">
              <div className="accountUsername">
                <span className="mobileLabel">שם:</span>
                <span className="accountValue">{getAgentFullName(agent)}</span>
              </div>
              <div className="accountEmail">
                <span className="mobileLabel">דוא"ל:</span>
                <span className="accountValue">{agent.email}</span>
              </div>
              <div className="accountActions">
                <span className="mobileLabel">פעולות:</span>
                <div className="actionsButtonsContainer">
                  {/* 0007: suspend/activate — icon + color reflect CURRENT state. Active agent shows
                      a pause action (click → suspend); suspended shows a red play action (click →
                      activate). This is the real account-status control (IsActive). */}
                  <button
                    className={`actionButton statusButton ${agent.isActive ? "isActive" : "isSuspended"}`}
                    title={agent.isActive ? "השהה סוכן" : "הפעל סוכן"}
                    onClick={() => handleToggleActive(agent)}
                  >
                    <i className={`fa-solid ${agent.isActive ? "fa-pause" : "fa-play"}`}></i>
                  </button>
                  {/* 0007: add-customers PERMISSION (CanAddCustomers) — deliberately a DIFFERENT
                      icon/shape so it can never be confused with suspension. */}
                  <button
                    className={`actionButton permissionButton ${agent.canAddCustomers ? "isAllowed" : "isBlocked"}`}
                    title={agent.canAddCustomers ? "רשאי להוסיף לקוחות" : "חסום מהוספת לקוחות"}
                    onClick={() => handleTogglePermissions(agent)}
                  >
                    <i className={`fa-solid ${agent.canAddCustomers ? "fa-user-plus" : "fa-user-slash"}`}></i>
                  </button>
                  <button className="actionButton updateButton" title="עריכת פרטים" onClick={() => handleEdit(agent.id)}>
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <button className="actionButton updateButton" title="שיוך לקוחות" onClick={() => handleAssign(agent.id)}>
                    <i className="fa-solid fa-people-arrows"></i>
                  </button>
                  <button className="actionButton updateButton" title="איפוס סיסמה" onClick={() => handleResetPassword(agent.id)}>
                    <i className="fa-solid fa-key"></i>
                  </button>
                  <button className="actionButton deleteButton" title="מחיקה" onClick={() => handleDelete(agent.id)}>
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="dialogContainer manageAccountsDialogContainer">
      <h1>ניהול סוכנים</h1>
      {getNewAgentContainerElement()}
      <div className="searchContainer">{getSearchInput()}</div>
      {getExistingAgentsContainerElement()}
    </div>
  );
};

export default ManageAgentsDialogComponent;
